import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";
import { Link } from "react-router-dom";
import Collapse from "react-bootstrap/Collapse";
import Select from "react-select";
import Swal from "sweetalert2";

import CashbookAddModal from "./CashbookAddModal";
import Pagination from "../Pagenation/Pagenation";

import CashbookServices from "../../../services/CashbookServices";
import LocationServices from "../../../services/LocationServices";

const readData = (response) => {
    const body = response?.data?.status
        ? response.data
        : response;

    return body?.data;
};

const rowOptions = [5, 10, 20, 50, 100].map((value) => ({
    value,
    label: String(value)
}));

const CashbookManagement = () => {
    const [filterOpen, setFilterOpen] = useState(false);
    const [listOpen, setListOpen] = useState(true);
    const [activeTab, setActiveTab] = useState("active");
    const [showAddModal, setShowAddModal] = useState(false);

    const [stores, setStores] = useState([]);
    const [cashbooks, setCashbooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [storesLoading, setStoresLoading] = useState(true);
    const [storesError, setStoresError] = useState("");
    const [error, setError] = useState("");
    const [changingId, setChangingId] = useState("");

    const [searchTerm, setSearchTerm] = useState("");
    const [storeFilter, setStoreFilter] = useState("");
    const [pageSize, setPageSize] = useState(100);
    const [page, setPage] = useState(0);

    const fetchCashbooks = useCallback(async () => {
        setLoading(true);
        setError("");

        try {
            const response = await CashbookServices.getPassbooks();
            const data = readData(response);

            if (!Array.isArray(data?.passbooks)) {
                throw new Error("Unexpected cashbook API response.");
            }

            setCashbooks(data.passbooks);
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                err?.message ||
                "Unable to load cashbooks.";

            setError(message);

            await Swal.fire({
                icon: "error",
                title: "Unable to Load Cashbooks",
                text: message,
                confirmButtonText: "OK"
            });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCashbooks();
    }, [fetchCashbooks]);

    useEffect(() => {
        let cancelled = false;

        const loadStores = async () => {
            setStoresLoading(true);
            setStoresError("");

            try {
                const response = await LocationServices.getStore();
                const allStores = response?.data?.data;

                if (!Array.isArray(allStores)) {
                    throw new Error("Unexpected store API response.");
                }

                if (!cancelled) {
                    setStores(allStores);
                }
            } catch (err) {
                if (!cancelled) {
                    const message =
                        err?.response?.data?.message ||
                        err?.message ||
                        "Unable to load stores.";

                    setStoresError(message);

                    Swal.fire(
                        "Unable to Load Stores",
                        message,
                        "error"
                    );
                }
            } finally {
                if (!cancelled) setStoresLoading(false);
            }
        };

        loadStores();

        return () => {
            cancelled = true;
        };
    }, []);

    const storeOptions = useMemo(() => {
        const storeMap = new Map();

        stores.forEach((store) => {
            storeMap.set(String(store._id), store);
        });

        cashbooks.forEach((cashbook) => {
            if (cashbook.store?._id) {
                storeMap.set(
                    String(cashbook.store._id),
                    cashbook.store
                );
            }
        });

        return [
            { value: "", label: "All Stores" },
            ...Array.from(storeMap.values())
                .sort((a, b) =>
                    String(a.name || "").localeCompare(
                        String(b.name || "")
                    )
                )
                .map((store) => ({
                    value: String(store._id),
                    label: `${store.name}${store.location ? `, ${store.location}` : ""
                        }`
                }))
        ];
    }, [stores, cashbooks]);

    const filteredCashbooks = useMemo(() => {
        const search = searchTerm.trim().toLowerCase();

        return cashbooks.filter((cashbook) => {
            if (cashbook.status !== activeTab) return false;

            if (
                storeFilter &&
                String(cashbook.store?._id || "") !== storeFilter
            ) {
                return false;
            }

            const searchableText = [
                cashbook.employee?.name,
                cashbook.employee?.email,
                cashbook.employee?.mobileNo,
                cashbook.store?.name,
                cashbook.store?.location,
                cashbook.store?.storeCode,
                cashbook.remark
            ]
                .filter((value) => value !== null && value !== undefined)
                .join(" ")
                .toLowerCase();

            return !search || searchableText.includes(search);
        });
    }, [cashbooks, activeTab, searchTerm, storeFilter]);

    const totalPages = Math.max(
        1,
        Math.ceil(filteredCashbooks.length / pageSize)
    );

    const currentPage = Math.min(
        Math.max(0, page),
        totalPages - 1
    );

    const start = currentPage * pageSize;

    const visibleCashbooks = filteredCashbooks.slice(
        start,
        start + pageSize
    );

    useEffect(() => {
        setPage(0);
    }, [activeTab, searchTerm, storeFilter, pageSize]);

    const handlePageChange = (nextPage) => {
        const pageNumber = Number(nextPage);

        if (!Number.isInteger(pageNumber)) return;

        setPage(
            Math.min(
                Math.max(0, pageNumber),
                totalPages - 1
            )
        );
    };

    const toggleStatus = async (cashbook) => {
        if (changingId || loading) return;

        const nextStatus =
            cashbook.status === "active" ? "inactive" : "active";

        const result = await Swal.fire({
            title: `Mark cashbook ${nextStatus}?`,
            text: `${cashbook.employee?.name || "Employee"} — ${cashbook.store?.name || "Store"
                }`,
            icon: "question",
            showCancelButton: true,
            confirmButtonText:
                nextStatus === "active" ? "Activate" : "Deactivate"
        });

        if (!result.isConfirmed) return;

        try {
            setChangingId(cashbook._id);

            await CashbookServices.updatePassbookStatus(
                cashbook._id,
                nextStatus
            );

            await fetchCashbooks();
        } catch (err) {
            await Swal.fire(
                "Error",
                err?.response?.data?.message ||
                "Unable to update cashbook status.",
                "error"
            );
        } finally {
            setChangingId("");
        }
    };

    const handleCreated = async () => {
        setShowAddModal(false);
        setActiveTab("active");
        setSearchTerm("");
        setStoreFilter("");
        setPage(0);
        setListOpen(true);

        await fetchCashbooks();
    };

    const tabStyle = (tab) => ({
        border: "none",
        padding: "8px 18px",
        borderRadius: "8px",
        background:
            activeTab === tab ? "#0d6efd" : "transparent",
        color:
            activeTab === tab ? "#fff" : "#495057",
        fontSize: "13px",
        fontWeight: 600,
        cursor: "pointer",
        transition: "0.2s ease",
        whiteSpace: "nowrap"
    });

    const renderHeading = () => (
        <div className="cpa">
            <i className="fa fa-list me-2" aria-hidden="true" />
            {activeTab === "active"
                ? "Active Cashbooks"
                : "Inactive Cashbooks"}

            <span className="badge bg-orange ms-2">
                {filteredCashbooks.length}
            </span>
        </div>
    );

    const renderListToggle = () => (
        <Link
            to="#"
            className={`SlideToolHeader ${listOpen ? "collapse" : "expand"
                }`}
            onClick={(event) => {
                event.preventDefault();
                setListOpen((previous) => !previous);
            }}
            aria-label="Toggle cashbook list"
            aria-expanded={listOpen}
        >
            <i className="fas fa-angle-up" aria-hidden="true" />
        </Link>
    );

    return (
        <>
            <CashbookAddModal
                show={showAddModal}
                handleClose={() => setShowAddModal(false)}
                onCreated={handleCreated}
            />

            <div className="row">
                <div className="col-xl-12">
                    {/* Filter panel */}
                    <div className="filter cm-content-box box-primary">
                        <div className="content-title">
                            <div className="cpa">
                                <i className="fas fa-filter me-2" />
                                Filter
                            </div>

                            <div className="tools">
                                <Link
                                    to="#"
                                    className={`SlideToolHeader ${filterOpen ? "collapse" : "expand"
                                        }`}
                                    onClick={(event) => {
                                        event.preventDefault();
                                        setFilterOpen((previous) => !previous);
                                    }}
                                    aria-label="Toggle filters"
                                    aria-expanded={filterOpen}
                                >
                                    <i className="fas fa-angle-up" />
                                </Link>
                            </div>
                        </div>

                        <Collapse in={filterOpen}>
                            <div className="cm-content-body form excerpt">
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-xl-4">
                                            <input
                                                type="text"
                                                className="form-control mb-xl-0 mb-3"
                                                placeholder="Search employee, mobile, store or remark..."
                                                value={searchTerm}
                                                onChange={(event) =>
                                                    setSearchTerm(event.target.value)
                                                }
                                                aria-label="Search cashbooks"
                                            />
                                        </div>

                                        <div className="col-xl-4">
                                            <Select
                                                options={storeOptions}
                                                value={storeOptions.find(
                                                    (option) => option.value === storeFilter
                                                )}
                                                onChange={(selected) =>
                                                    setStoreFilter(selected?.value || "")
                                                }
                                                isSearchable
                                                className="custom-react-select mb-xl-0 mb-3"
                                                placeholder="All Stores"
                                            />
                                        </div>

                                        <div className="col-xl-4">
                                            <Select
                                                options={rowOptions}
                                                value={rowOptions.find(
                                                    (option) => option.value === pageSize
                                                )}
                                                onChange={(selected) =>
                                                    setPageSize(selected?.value || 100)
                                                }
                                                isSearchable={false}
                                                className="custom-react-select mb-3 mb-xl-0"
                                                placeholder="Rows to display"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Collapse>
                    </div>

                    {/* Cashbook list panel */}
                    <div className="filter cm-content-box box-primary">
                        <div className="content-title d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
                            {/* Mobile heading */}
                            <div className="d-flex justify-content-between align-items-center w-100 d-md-none">
                                {renderHeading()}

                                <div className="tools">
                                    {renderListToggle()}
                                </div>
                            </div>

                            {/* Desktop heading */}
                            <div className="text-center text-md-start d-none d-md-block">
                                {renderHeading()}
                            </div>

                            {/* Center tabs */}
                            <div
                                className="d-flex align-items-center flex-nowrap order-2 order-md-0 align-self-start"
                                style={{
                                    background: "#f1f3f5",
                                    padding: "5px",
                                    borderRadius: "12px",
                                    border: "1px solid #dee2e6",
                                    maxWidth: "100%",
                                    overflowX: "auto"
                                }}
                            >
                                <button
                                    type="button"
                                    style={tabStyle("active")}
                                    onClick={() => setActiveTab("active")}
                                >
                                    Active Cashbooks
                                </button>

                                <button
                                    type="button"
                                    className="ms-2"
                                    style={tabStyle("inactive")}
                                    onClick={() => setActiveTab("inactive")}
                                >
                                    Inactive Cashbooks
                                </button>
                            </div>

                            {/* Desktop collapse control */}
                            <div className="tools d-none d-md-flex align-items-center justify-content-center">
                                {renderListToggle()}
                            </div>
                        </div>

                        {/* Single Add button */}
                        <div className="row me-0 me-md-3">
                            <div className="col-12 d-flex w-100 justify-content-start justify-content-md-end ms-3 ms-md-0 me-md-4 mb-2">
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm d-flex align-items-center"
                                    onClick={() => setShowAddModal(true)}
                                >
                                    <i className="fas fa-plus me-2" />
                                    Add New Cashbook
                                </button>
                            </div>
                        </div>



                        <Collapse in={listOpen}>
                            <div className="cm-content-body form excerpt">
                                <div className="card-body">

                                    <div className="table-responsive">
                                        <div className="dataTables_wrapper no-footer">
                                            <div className="scrolltable">
                                                <table
                                                    className="table table-bordered table-responsive-lg table-striped table-condensed flip-content"
                                                    id="cashbooks-data"
                                                >
                                                    <thead className="sticky-table-header bg-white">
                                                        <tr>
                                                            <th>S.No</th>
                                                            <th>Employee</th>
                                                            <th>Email</th>
                                                            <th>Store</th>
                                                            <th>Location</th>
                                                            <th>Mobile No.</th>
                                                            <th>Remark</th>
                                                            <th>Created On</th>
                                                            <th>Status</th>
                                                            <th className="text-center">Actions</th>
                                                        </tr>
                                                    </thead>

                                                    <tbody>
                                                        {loading ? (
                                                            <tr>
                                                                <td
                                                                    colSpan={10}
                                                                    className="text-center py-4"
                                                                >
                                                                    <span
                                                                        className="spinner-border spinner-border-sm me-2"
                                                                        aria-hidden="true"
                                                                    />
                                                                    Loading cashbooks...
                                                                </td>
                                                            </tr>
                                                        ) : visibleCashbooks.length === 0 ? (
                                                            <tr>
                                                                <td
                                                                    colSpan={10}
                                                                    className="text-center"
                                                                >
                                                                    {error
                                                                        ? "Cashbooks could not be loaded. Please refresh and try again."
                                                                        : `No ${activeTab} cashbooks found`}
                                                                </td>
                                                            </tr>
                                                        ) : (
                                                            visibleCashbooks.map(
                                                                (cashbook, index) => (
                                                                    <tr key={cashbook._id}>
                                                                        <td>{start + index + 1}</td>

                                                                        <td>
                                                                            {cashbook.employee?.name || "-"}
                                                                        </td>

                                                                        <td>
                                                                            {cashbook.employee?.email || "-"}
                                                                        </td>

                                                                        <td className="scrollable-description">
                                                                            {cashbook.store?.name || "-"}
                                                                        </td>

                                                                        <td>
                                                                            {cashbook.store?.location || "-"}
                                                                        </td>

                                                                        <td>
                                                                            {cashbook.employee?.mobileNo || "-"}
                                                                        </td>

                                                                        <td className="scrollable-description">
                                                                            {cashbook.remark || "-"}
                                                                        </td>

                                                                        <td>
                                                                            {cashbook.createdAt
                                                                                ? new Date(
                                                                                    cashbook.createdAt
                                                                                ).toLocaleDateString("en-IN")
                                                                                : "-"}
                                                                        </td>

                                                                        <td>
                                                                            <i
                                                                                className={`fa fa-circle ${cashbook.status === "active"
                                                                                    ? "text-success"
                                                                                    : "text-danger"
                                                                                    }`}
                                                                                title={
                                                                                    cashbook.status === "active"
                                                                                        ? "Active"
                                                                                        : "Inactive"
                                                                                }
                                                                                aria-label={
                                                                                    cashbook.status === "active"
                                                                                        ? "Active"
                                                                                        : "Inactive"
                                                                                }
                                                                            />
                                                                        </td>

                                                                        <td className="text-center">
                                                                            <button
                                                                                type="button"
                                                                                className={`btn btn-xs ${cashbook.status === "active"
                                                                                    ? "btn-danger"
                                                                                    : "btn-primary"
                                                                                    }`}
                                                                                disabled={
                                                                                    Boolean(changingId) || loading
                                                                                }
                                                                                onClick={() =>
                                                                                    toggleStatus(cashbook)
                                                                                }
                                                                            >
                                                                                {changingId === cashbook._id
                                                                                    ? "Updating..."
                                                                                    : cashbook.status === "active"
                                                                                        ? "Deactivate"
                                                                                        : "Activate"}
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                )
                                                            )
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>

                                            {/* Same shared pagination as Location */}
                                            {!loading && (
                                                <div className="d-sm-flex text-center justify-content-between align-items-center mt-3">
                                                    <div className="dataTables_info">
                                                        Showing{" "}
                                                        {filteredCashbooks.length > 0
                                                            ? start + 1
                                                            : 0}
                                                        {" "}to{" "}
                                                        {Math.min(
                                                            filteredCashbooks.length,
                                                            start + pageSize
                                                        )}
                                                        {" "}of {filteredCashbooks.length} entries
                                                    </div>

                                                    <Pagination
                                                        activePage={currentPage}
                                                        totalPages={totalPages}
                                                        onPageChange={handlePageChange}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Collapse>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CashbookManagement;